/**
 * Cached accessor for the `systemSettings/roleConfig` Firestore doc
 * (Task #15).
 *
 * Pre-Task-#15, three sets of identifiers were hard-coded across the
 * client AND firestore.rules:
 *   - super-admin UID `162p0OibioOq7BkhAYserTYXTqg1`
 *   - admin role-doc ID `QAmMRbVe7MJSqg4uxMvF`
 *   - coach role-doc ID `uaE4WDmuLHbiUh6Z47w8`
 *   - coordinator role-doc ID `e2OsBVBtr4pHUxEfXoTL`
 *
 * The canonical source is now `config/roleConfig.json`, which:
 *   1. Bakes the same values in as DEFAULTS here so the app works
 *      even when the Firestore doc hasn't been seeded yet (e.g. fresh
 *      install, missing migration). The defaults match the historical
 *      hard-coded values exactly — so behavior is identical to pre-#15
 *      until an admin overrides via the doc.
 *   2. Is the input to `scripts/generate-rules.mjs`, which writes
 *      `firestore.rules` (rules can't `get()` a config doc per
 *      evaluation cheaply, so the IDs live in the rules file directly
 *      but derived from the same single edit point).
 *   3. Seeds the `systemSettings/roleConfig` Firestore doc (the live
 *      override mechanism for the running app).
 *
 * --- API SHAPE -----------------------------------------------------
 *
 * Sync accessor: `getRoleConfig(): RoleConfig`. Always returns
 * immediately; if the live doc hasn't been fetched yet, returns the
 * baked-in defaults. The first call from any module triggers an async
 * fetch of `systemSettings/roleConfig` in the background; once that
 * resolves, subsequent `getRoleConfig()` calls return the live values.
 *
 * This pattern lets existing sync callers (`isCoordinator(user)`,
 * `isUserGamified(uid)`, attendance whitelist) stay sync without a
 * ripple refactor across the codebase. The trade-off: there's a small
 * window at app boot where calls see defaults — which is harmless,
 * because the defaults ARE the production values.
 *
 * Production seeding: see CLAUDE.md §11.15 — one-time creation of the
 * `systemSettings/roleConfig` doc with the same shape as the JSON.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import canonical from '../../config/roleConfig.json';

export interface RoleConfig {
    superAdminUids: ReadonlyArray<string>;
    adminRoleIds: ReadonlyArray<string>;
    coachRoleIds: ReadonlyArray<string>;
    coordinatorRoleIds: ReadonlyArray<string>;
}

/**
 * Defaults — read at build time from `config/roleConfig.json`. The
 * file's `_doc` and any other keys are stripped; only the four
 * recognized arrays are surfaced.
 */
export const DEFAULT_ROLE_CONFIG: RoleConfig = {
    superAdminUids: [...(canonical.superAdminUids ?? [])],
    adminRoleIds: [...(canonical.adminRoleIds ?? [])],
    coachRoleIds: [...(canonical.coachRoleIds ?? [])],
    coordinatorRoleIds: [...(canonical.coordinatorRoleIds ?? [])],
};

let cached: RoleConfig = DEFAULT_ROLE_CONFIG;
let fetchPromise: Promise<void> | null = null;
let didFail = false;

/**
 * Coerce one of the four expected fields from raw doc data. Drops
 * non-strings, dedupes, and falls back to the defaults when missing
 * or empty so an incomplete doc never locks the app out.
 */
function coerceList(value: unknown, fallback: ReadonlyArray<string>): ReadonlyArray<string> {
    if (!Array.isArray(value)) return fallback;
    const strs = value.filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (strs.length === 0) return fallback;
    return Array.from(new Set(strs));
}

/**
 * Kick off the live-doc fetch. Safe to call multiple times — the
 * promise is memoized, so concurrent callers all await the same
 * underlying `getDoc`.
 */
function loadFromFirestore(): Promise<void> {
    if (fetchPromise) return fetchPromise;
    fetchPromise = (async () => {
        try {
            const snap = await getDoc(doc(db, 'systemSettings', 'roleConfig'));
            if (!snap.exists()) {
                // Doc not seeded yet — keep DEFAULTS.
                return;
            }
            const data = snap.data() as Partial<RoleConfig>;
            cached = {
                superAdminUids: coerceList(data.superAdminUids, DEFAULT_ROLE_CONFIG.superAdminUids),
                adminRoleIds: coerceList(data.adminRoleIds, DEFAULT_ROLE_CONFIG.adminRoleIds),
                coachRoleIds: coerceList(data.coachRoleIds, DEFAULT_ROLE_CONFIG.coachRoleIds),
                coordinatorRoleIds: coerceList(data.coordinatorRoleIds, DEFAULT_ROLE_CONFIG.coordinatorRoleIds),
            };
        } catch (err) {
            // Network / rules denial / quota — defaults stay in place.
            // Log once so misconfig is visible in console but don't
            // throw: the app must continue functioning.
            didFail = true;
            // eslint-disable-next-line no-console
            console.warn('[roleConfig] failed to load systemSettings/roleConfig, using defaults:', err);
        }
    })();
    return fetchPromise;
}

/**
 * Sync accessor — returns the current cached `RoleConfig`. On first
 * call, kicks off an async fetch of the live doc; until it resolves,
 * returns `DEFAULT_ROLE_CONFIG` (which mirrors the values baked into
 * `config/roleConfig.json`).
 *
 * Subsequent calls return the live values once the fetch lands.
 */
export function getRoleConfig(): RoleConfig {
    // Fire-and-forget: triggers the async load on first call.
    void loadFromFirestore();
    return cached;
}

/**
 * Awaitable variant — for callers that DO want to wait for the live
 * doc (e.g. test setup, server-style scripts, an explicit
 * `await initRoleConfig()` at app boot).
 */
export async function initRoleConfig(): Promise<RoleConfig> {
    await loadFromFirestore();
    return cached;
}

/**
 * Test-only reset. Clears the cache + memoized promise so a unit test
 * can simulate "first call" behavior between cases.
 *
 * Not exported through `index.ts`-style barrels; consumers must import
 * by name. Production code should never call this.
 */
export function __resetRoleConfigForTests(): void {
    cached = DEFAULT_ROLE_CONFIG;
    fetchPromise = null;
    didFail = false;
}

/**
 * Test-only inspector for the "did the fetch fail" flag. Used by the
 * unit tests to assert error-path behavior without poking internals.
 */
export function __didRoleConfigFetchFail(): boolean {
    return didFail;
}
